import React from 'react';
import clsx from 'clsx';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import ArticleIcon from '@mui/icons-material/Article';
import DateFnsUtils from '@date-io/date-fns';

import config from '../../config';
import { GridLinkButton } from 'components/common/GridLinkButton';
import { DEFAULT_DATE_FORMAT } from '../../constants/reports';

const dateFns = new DateFnsUtils();

const BadgeCell = styled(Box)(({ theme }) => ({
  color: '#fff',
  borderRadius: '4px',
  padding: '2px 6px',
  display: 'inline-block',
  textTransform: 'capitalize',
  fontSize: '13px',
}));

const StatusCell = styled(BadgeCell)(({ theme }) => ({
  '&[data-status="new" i]': {
    backgroundColor: '#f6ab2e',
  },
  '&[data-status="active" i]': {
    backgroundColor: '#3ed436',
  },
  '&[data-status="paused" i]': {
    backgroundColor: '#f6ab2e',
  },
  '&[data-status="removed" i]': {
    backgroundColor: '#D47483',
  },
  '&[data-status]': {
    transition: 'background-color 0.2s ease',
  },
}));
const CategoryCell = styled(BadgeCell)(({ theme }) => ({
  '&[data-category="MAINSTREAM" i]': {
    backgroundColor: '#f6ab2e',
  },
  '&[data-category="MIXED" i]': {
    backgroundColor: '#3ed436',
  },
  '&[data-category="ADULT" i]': {
    backgroundColor: '#D47483',
  },
  '&[data-category]': {
    transition: 'background-color 0.2s ease',
  },
}));
/**
 * Returns a spend status className based on percentage.
 *
 * @param {number} percentage - The spend percentage (0–100).
 * @param {string} [prefix='balance-spend-status'] - Optional base class name.
 * @returns {string} Combined className string.
 */
const getSpendStatusClassName = (percentage, prefix = 'balance-spend-status') =>
  clsx(prefix, {
    low: percentage < 50,
    half: percentage >= 50 && percentage < 75,
    high: percentage >= 75,
  });

export const ENDPOINTS_GRID_COLUMNS = ({
  handleTrafficRequest,
  handleTrafficResponse,
}) => [
  {
    accessorKey: 'id',
    header: 'Name',
    Cell: ({ cell }) => {
      const rowData = cell.row.original;
      const displayText = `(${rowData.id}) ${rowData.name}`;
      return (
        <Tooltip title={displayText} placement='top'>
          <GridLinkButton
            component={RouterLink}
            to={`/endpoints/${rowData.id}/edit`}
          >
            {displayText}
          </GridLinkButton>
        </Tooltip>
      );
    },
  },
  {
    accessorKey: 'adType',
    header: 'Ad Type',
  },
  {
    accessorKey: 'category',
    header: 'Category',
    Cell: ({ cell }) => (
      <CategoryCell data-category={cell.getValue()}>
        {cell.getValue()}
      </CategoryCell>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    Cell: ({ cell }) => (
      <StatusCell data-status={cell.getValue()}>{cell.getValue()}</StatusCell>
    ),
  },
  {
    accessorKey: 'hourlySpend',
    header: 'Hourly Spend',
    Cell: ({ cell }) => {
      const { hourlyBudget, hourlySpend, hourlySpendPercentage } =
        cell.row.original;

      if (!hourlyBudget) {
        return null;
      }

      const balanceSpendClassName = getSpendStatusClassName(
        hourlySpendPercentage,
      );

      return (
        <span className={balanceSpendClassName}>
          {hourlySpend} / {hourlyBudget}
        </span>
      );
    },
    enableSorting: false,
  },
  {
    accessorKey: 'dailySpend',
    header: 'Daily Spend',
    Cell: ({ cell }) => {
      const { dailyBudget, dailySpend, dailySpendPercentage } =
        cell.row.original;

      if (!dailyBudget) {
        return null;
      }

      const balanceSpendClassName =
        getSpendStatusClassName(dailySpendPercentage);

      return (
        <span className={balanceSpendClassName}>
          {dailySpend} / {dailyBudget}
        </span>
      );
    },
    enableSorting: false,
  },
  {
    accessorKey: 'qpsThreshold',
    header: 'Max QPS',
  },
  {
    accessorKey: 'avgQps',
    header: 'Avg QPS',
    Footer: ({ table }) => {
      const totals = table.options.meta?.totals || {};
      const avgQps = totals.avgQps || 0;

      return (
        <Typography sx={{ fontWeight: 'bold', fontSize: '14px' }}>
          {avgQps}
        </Typography>
      );
    },
  },
  {
    accessorKey: 'tqps',
    header: 'TQPS',
    Footer: ({ table }) => {
      const totals = table.options.meta?.totals || {};
      const totalTqps = totals.tqps || 0;

      return (
        <Typography sx={{ fontWeight: 'bold', fontSize: '14px' }}>
          {totalTqps}
        </Typography>
      );
    },
  },
  {
    accessorKey: 'qps',
    header: 'QPS',
    Footer: ({ table }) => {
      const totals = table.options.meta?.totals || {};
      const totalQps = totals.qps || 0;

      return (
        <Typography sx={{ fontWeight: 'bold', fontSize: '14px' }}>
          {totalQps}
        </Typography>
      );
    },
  },
  {
    accessorKey: 'createdAt',
    header: 'Registered',
    Cell: ({ cell }) =>
      dateFns.format(new Date(cell.getValue()), DEFAULT_DATE_FORMAT),
  },
  {
    header: 'Actions',
    size: 120,
    Cell: ({ cell }) => {
      const id = cell.row.original.id;

      return (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip
            title='Get traff response'
            placement='top'
            key={`resp-${id}`}
          >
            <Box
              sx={{ cursor: 'pointer', color: '#3f51b5' }}
              onClick={() => handleTrafficRequest(id)}
            >
              <PlayCircleIcon />
            </Box>
          </Tooltip>

          <Tooltip title='Get traff request' placement='top' key={`req-${id}`}>
            <Box
              sx={{ cursor: 'pointer', color: '#3f51b5' }}
              onClick={() => handleTrafficResponse(id)}
            >
              <PlayCircleIcon />
            </Box>
          </Tooltip>

          <a
            href={`${config.REST_API}/endpoint-${id}.txt`}
            target='_blank'
            rel='noreferrer'
            style={{ color: '#3f51b5' }}
            key={`file-${id}`}
          >
            <ArticleIcon />
          </a>
        </Box>
      );
    },
    enableSorting: false,
  },
];
